import type { ReactNode } from 'react'
import React, { useRef, useState, useMemo } from 'react'
import { Provider, Root } from '@radix-ui/react-tooltip'
import debug from 'debug'

import { findOneByName } from '@s4l/utils/library/react'

import { Content } from './Content'
import { Trigger } from './Trigger'
import { TooltipContext } from './context'

const log = debug('s4l:ds:Tooltip')

export interface ITooltip {
  children: ReactNode
  /** Initially open */
  initialOpen?: boolean
  /** Callback to be called when the tooltip is opened or closed */
  onOpenChange?: (open: boolean) => void
  /** Delay to open from the trigger in milliseconds. Default: 200 */
  delayOpen?: number
  /** Delay to close from the trigger in milliseconds. Default: 1500 */
  delayClose?: number
  /** Allows render only the trigger if is false. Default: true */
  enabled?: boolean
}
function Tooltip(props: Readonly<ITooltip>) {
  const {
    children,
    initialOpen = false,
    onOpenChange,
    delayOpen = 200,
    delayClose = 1500,
    enabled = true,
  } = props
  const contextValue = useMemo(() => ({ enabled }), [enabled])

  const [internalOpen, setInternalOpen] = useState(initialOpen)
  const timeoutRef = useRef<NodeJS.Timeout>()

  const trigger = findOneByName(children, 'Tooltip.Trigger')
  if (!trigger) {
    log('Tooltip.Trigger is required')
    return null
  }

  const content = findOneByName(children, 'Tooltip.Content')
  if (!content) {
    log('Tooltip.Content is required')
    return null
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    const delay = newOpen ? 0 : delayClose

    if (delay === 0) {
      setInternalOpen(newOpen)
      onOpenChange?.(newOpen)
    } else {
      timeoutRef.current = setTimeout(() => {
        setInternalOpen(newOpen)
        onOpenChange?.(newOpen)
      }, delay)
    }
  }

  return (
    <TooltipContext.Provider value={contextValue}>
      <Provider delayDuration={delayOpen}>
        <Root open={internalOpen} onOpenChange={handleOpenChange}>
          {children}
        </Root>
      </Provider>
    </TooltipContext.Provider>
  )
}

Tooltip.Content = Content
Tooltip.Trigger = Trigger

export default Tooltip
